import { useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import type { LucideProps } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface FeatureFlipCardProps {
  icon: ComponentType<LucideProps>;
  title: string;
  text: string;
  /**
   * Image/GIF/video for the back side. Put files in `client/public/features/`
   * and use a public path, for example `/features/compass.gif` or `/features/voice.mp4`.
   * If omitted, the back side uses only gradients and the large icon.
   */
  media?: string;
}

export function FeatureFlipCard({ icon: Icon, title, text, media }: FeatureFlipCardProps) {
  const [tapped, setTapped] = useState(false);
  const [hovered, setHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const isVideo = useMemo(() => /\.(mp4|webm|m4v|mov)(?:[?#].*)?$/i.test(media ?? ''), [media]);
  const active = hovered || tapped;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    if (active) {
      video.currentTime = 0;
      void video.play().catch(() => undefined);
      return;
    }

    video.pause();
    video.currentTime = 0;
  }, [active]);

  return (
    <div
      className="group shrink-0 w-80 h-80 [perspective:1200px] cursor-pointer select-none"
      onClick={() => setTapped((v) => !v)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => {
        setHovered(false);
        setTapped(false);
      }}
    >
      <div
        className={cn(
          'relative h-full w-full transition-transform duration-700 ease-out [transform-style:preserve-3d]',
          'group-hover:[transform:rotateY(180deg)]',
          tapped && '[transform:rotateY(180deg)]',
        )}
      >
        {/* Лицо */}
        <div className="absolute inset-0 rounded-2xl border border-white/5 bg-eol-black/60 p-7 [backface-visibility:hidden] flex flex-col">
          <Icon className="h-9 w-9 text-eol-pink mb-4" />
          <h3 className="font-extrabold text-eol-white text-xl mb-2">{title}</h3>
          <p className="text-sm text-eol-desc leading-relaxed flex-1">{text}</p>
          <p className="mt-3 text-xs text-eol-pink/70 uppercase tracking-wider font-semibold">
            Наведи →
          </p>
        </div>

        {/* Обратная сторона */}
        <div
          className="absolute inset-0 overflow-hidden rounded-2xl border border-eol-pink/30 [backface-visibility:hidden] [transform:rotateY(180deg)]"
        >
          {media && isVideo ? (
            <video
              ref={videoRef}
              src={media}
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => {
                (e.currentTarget as HTMLVideoElement).style.display = 'none';
              }}
            />
          ) : media ? (
            <img
              src={media}
              alt=""
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover"
              onError={(e) => {
                // если файл не положен - просто скроем картинку, останется градиент
                (e.currentTarget as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : null}
          <div className="absolute inset-0 bg-gradient-to-br from-eol-pink/30 via-transparent to-eol-pink/10" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent" />

          <div className="relative h-full flex flex-col justify-end p-7">
            <Icon className="h-10 w-10 text-eol-pink mb-3 drop-shadow-[0_0_12px_rgba(255,149,206,0.6)]" />
            <h3 className="font-extrabold text-eol-white text-2xl leading-tight">{title}</h3>
            <p className="mt-2 text-xs text-eol-pink/90 uppercase tracking-wider font-semibold">
              Подробнее на сервере →
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
